import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import type { RootState } from "../../store/store";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { deleteAdCascadeApi, deleteBookingsByUserIdApi, getPaginatedAdsApi, searchAdsForTopPositionApi, type TopAdSearchItem, updateAdRateApi, updateAdStatusApi, updateAdTopPositionApi } from "../../api/adsApi";
import { listCommentsForModerationApi, type AdCommentDto, validateCommentByIdApi } from "../../api/commentApi";
import { listRateAdsForModerationApi, type RateAdDto, validateRateAdByIdApi } from "../../api/rateAdApi";
import { listOwnerReportsForModerationApi, type OwnerReportDto } from "../../api/reportApi";
import { deleteAdminUserCascadeApi, getAdminUserLoginLocationsApi, searchAdminUsersApi, type AdminUserSearchResult, type AdminUserSort, updateUserRoleByIdApi } from "../../api/userManagementApi";
import { getLocalizedText } from "../../features/ads/i18nFields";
import {
  FiActivity,
  FiBarChart2,
  FiCheckSquare,
  FiCpu,
  FiFlag,
  FiGlobe,
  FiGrid,
  FiHardDrive,
  FiMenu,
  FiMessageSquare,
  FiPieChart,
  FiShield,
  FiTrash2,
  FiUsers,
  FiXCircle,
  FiX,
} from "react-icons/fi";
import { auditAdminUploadImagesApi, deleteAuditedUploadImageApi, getAdminServerMetricsApi, type ServerMetricsPayload, type UploadAuditPayload } from "../../api/userManagementApi";
import { deleteCountryApi, getCountriesApi, postCountryApi, putCountryApi } from "../../api/countryApi";
import { getCountryDisplayName, getCountryValueDisplay, type Country } from "../../features/country/countrySlice";
import styles from "./Dashboard.module.css";

type DashboardGeoPoint = {
  id: string;
  kind?: "user" | "visitor";
  name: string;
  email: string;
  role: string;
  lastLoginAt: string | null;
  visitsCount?: number | null;
  location: {
    ip?: string;
    city: string;
    region: string;
    country: string;
    countryCode: string;
    latitude: number;
    longitude: number;
  };
};

type DashboardGeoCountryPoint = {
  key: string;
  country: string;
  countryCode: string;
  latitude: number;
  longitude: number;
  totalVisits: number;
  sampleUsers: number;
};

type DashboardSectionId =
  | "overview"
  | "server"
  | "delete-ad"
  | "roles"
  | "user-search"
  | "bookings"
  | "countries"
  | "geo"
  | "top-ads"
  | "ad-ratings"
  | "reports"
  | "pending-ads"
  | "comments"
  | "rate-reviews"
  | "search";

type CountryFormState = {
  name: string;
  indicatif: string;
  mask: string;
  code: string;
};

const GridIcon = FiGrid as unknown as React.ComponentType<{ "aria-hidden"?: boolean; focusable?: string }>;
const TrashIcon = FiTrash2 as unknown as React.ComponentType<{ "aria-hidden"?: boolean; focusable?: string }>;
const UsersIcon = FiUsers as unknown as React.ComponentType<{ "aria-hidden"?: boolean; focusable?: string }>;
const GlobeIcon = FiGlobe as unknown as React.ComponentType<{ "aria-hidden"?: boolean; focusable?: string }>;
const FlagIcon = FiFlag as unknown as React.ComponentType<{ "aria-hidden"?: boolean; focusable?: string }>;
const CheckSquareIcon = FiCheckSquare as unknown as React.ComponentType<{ "aria-hidden"?: boolean; focusable?: string }>;
const MessageIcon = FiMessageSquare as unknown as React.ComponentType<{ "aria-hidden"?: boolean; focusable?: string }>;
const ChartIcon = FiBarChart2 as unknown as React.ComponentType<{ "aria-hidden"?: boolean; focusable?: string }>;
const SearchIcon = FiBarChart2 as unknown as React.ComponentType<{ "aria-hidden"?: boolean; focusable?: string }>;
const ShieldIcon = FiShield as unknown as React.ComponentType<{ "aria-hidden"?: boolean; focusable?: string }>;
const ActivityIcon = FiActivity as unknown as React.ComponentType<{ "aria-hidden"?: boolean; focusable?: string }>;
const CpuIcon = FiCpu as unknown as React.ComponentType<{ "aria-hidden"?: boolean; focusable?: string }>;
const HardDriveIcon = FiHardDrive as unknown as React.ComponentType<{ "aria-hidden"?: boolean; focusable?: string }>;
const PieChartIcon = FiPieChart as unknown as React.ComponentType<{ "aria-hidden"?: boolean; focusable?: string }>;
const MenuIcon = FiMenu as unknown as React.ComponentType<{ "aria-hidden"?: boolean; focusable?: string }>;
const CloseIcon = FiX as unknown as React.ComponentType<{ "aria-hidden"?: boolean; focusable?: string }>;
const RemoveIcon = FiXCircle as unknown as React.ComponentType<{ "aria-hidden"?: boolean; focusable?: string }>;

const getCountryGroupingKey = (point: DashboardGeoPoint): string => {
  const countryCode = String(point.location.countryCode || "").trim().toUpperCase();
  const country = String(point.location.country || "").trim();
  return countryCode || country || `${point.location.latitude.toFixed(1)}:${point.location.longitude.toFixed(1)}`;
};

const COUNTRY_MARKER_CENTER: Record<string, { latitude: number; longitude: number }> = {
  FR: { latitude: 46.2276, longitude: 2.2137 },
  BE: { latitude: 50.5039, longitude: 4.4699 },
  CH: { latitude: 46.8182, longitude: 8.2275 },
  DE: { latitude: 51.1657, longitude: 10.4515 },
  ES: { latitude: 40.4637, longitude: -3.7492 },
  IT: { latitude: 41.8719, longitude: 12.5674 },
  GB: { latitude: 55.3781, longitude: -3.436 },
  MA: { latitude: 31.7917, longitude: -7.0926 },
  DZ: { latitude: 28.0339, longitude: 1.6596 },
  TN: { latitude: 33.8869, longitude: 9.5375 },
  US: { latitude: 37.0902, longitude: -95.7129 },
  CA: { latitude: 56.1304, longitude: -106.3468 },
  BR: { latitude: -14.235, longitude: -51.9253 },
  AR: { latitude: -38.4161, longitude: -63.6167 },
  TR: { latitude: 38.9637, longitude: 35.2433 },
  RU: { latitude: 61.524, longitude: 105.3188 },
  IN: { latitude: 20.5937, longitude: 78.9629 },
  CN: { latitude: 35.8617, longitude: 104.1954 },
  JP: { latitude: 36.2048, longitude: 138.2529 },
  AU: { latitude: -25.2744, longitude: 133.7751 },
};

const getGeoPointWeight = (point: DashboardGeoPoint): number => {
  const value = Number(point.visitsCount || 0);
  return Number.isFinite(value) && value > 1 ? value : 1;
};

const getGeoPointColor = (weight: number, maxWeight: number): string => {
  if (maxWeight <= 1) return "hsl(142 76% 42%)";
  const ratio = Math.max(0, Math.min(1, (weight - 1) / (maxWeight - 1)));
  const hue = 142 - ratio * 132;
  return `hsl(${hue.toFixed(1)} 82% 45%)`;
};

const formatDateTime = (value?: string, lang = "fr"): string => {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "-";
  if (lang === "fr") {
    const locale = "fr-FR";
    const weekday = new Intl.DateTimeFormat(locale, { weekday: "long" }).format(parsed);
    const dayMonthYear = new Intl.DateTimeFormat(locale, { day: "numeric", month: "long", year: "numeric" }).format(parsed);
    const hours = String(parsed.getHours()).padStart(2, "0");
    const minutes = String(parsed.getMinutes()).padStart(2, "0");
    return `${weekday.charAt(0).toUpperCase()}${weekday.slice(1)} ${dayMonthYear} à ${hours}h${minutes}`;
  }
  const locale = lang === "ar" ? "ar-DZ" : "en-US";
  return new Intl.DateTimeFormat(locale, {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(parsed);
};

const formatBytes = (value?: number, lang = "fr") => {
  const numericValue = Number(value || 0);
  if (!Number.isFinite(numericValue)) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let size = Math.abs(numericValue);
  let unitIndex = 0;
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex += 1;
  }
  const formatted = new Intl.NumberFormat(lang === "fr" ? "fr-FR" : lang === "ar" ? "ar-DZ" : "en-US", {
    maximumFractionDigits: size >= 100 ? 0 : size >= 10 ? 1 : 2,
  }).format(size);
  return `${formatted} ${units[unitIndex]}`;
};

const formatPercent = (value?: number, lang = "fr") => {
  const numericValue = Number(value || 0);
  if (!Number.isFinite(numericValue)) return "0%";
  const formatted = new Intl.NumberFormat(lang === "fr" ? "fr-FR" : lang === "ar" ? "ar-DZ" : "en-US", {
    maximumFractionDigits: 1,
  }).format(numericValue);
  return `${formatted}%`;
};

const formatDuration = (valueSeconds?: number, lang = "fr") => {
  const totalSeconds = Math.max(0, Math.round(Number(valueSeconds || 0)));
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  if (lang === "fr") {
    if (days > 0) return `${days} j ${hours} h`;
    return `${hours} h`;
  }
  if (lang === "ar") {
    if (days > 0) return `${days} ي ${hours} س`;
    return `${hours} س`;
  }
  if (days > 0) return `${days}d ${hours}h`;
  return `${hours}h`;
};

const buildLinePoints = (values: number[], width: number, height: number, padding = 12) => {
  if (!values.length) return "";
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  return values
    .map((value, index) => {
      const x = padding + (index * (width - padding * 2)) / Math.max(1, values.length - 1);
      const y = height - padding - ((value - min) / range) * (height - padding * 2);
      return `${x},${y}`;
    })
    .join(" ");
};

const DashboardPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const dashboardLang = (i18n.resolvedLanguage || "fr").split("-")[0];
  const user = useSelector((state: RootState) => state.auth.user);
  const [adId, setAdId] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [pendingAds, setPendingAds] = useState<Array<{ id: string; title: string; address: string }>>([]);
  const [pendingAdsLoading, setPendingAdsLoading] = useState(false);
  const [pendingAdToValidate, setPendingAdToValidate] = useState<{ id: string; title: string; address: string } | null>(null);
  const [validatingAd, setValidatingAd] = useState(false);
  const [topAdSearchText, setTopAdSearchText] = useState("");
  const [topAdSuggestions, setTopAdSuggestions] = useState<TopAdSearchItem[]>([]);
  const [selectedTopAd, setSelectedTopAd] = useState<TopAdSearchItem | null>(null);
  const [selectedTopPosition, setSelectedTopPosition] = useState<number>(1);
  const [topAdSearching, setTopAdSearching] = useState(false);
  const [topAdSaving, setTopAdSaving] = useState(false);
  const [ratingAdSearchText, setRatingAdSearchText] = useState("");
  const [ratingAdSuggestions, setRatingAdSuggestions] = useState<TopAdSearchItem[]>([]);
  const [selectedRatingAd, setSelectedRatingAd] = useState<TopAdSearchItem | null>(null);
  const [selectedAdRate, setSelectedAdRate] = useState<number>(1);
  const [ratingAdSearching, setRatingAdSearching] = useState(false);
  const [ratingAdSaving, setRatingAdSaving] = useState(false);
  const shouldRefreshPendingAdQuartierCoordinates = false;
  const setShouldRefreshPendingAdQuartierCoordinates = (_value: boolean) => {};
  const [pendingComments, setPendingComments] = useState<AdCommentDto[]>([]);
  const [rateReviews, setRateReviews] = useState<RateAdDto[]>([]);
  const [ownerReports, setOwnerReports] = useState<OwnerReportDto[]>([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [rateReviewsLoading, setRateReviewsLoading] = useState(false);
  const [rateReviewsLoadingMore, setRateReviewsLoadingMore] = useState(false);
  const [rateReviewsHasMore, setRateReviewsHasMore] = useState(false);
  const [rateReviewsNextCursor, setRateReviewsNextCursor] = useState<string | null>(null);
  const [ownerReportsLoading, setOwnerReportsLoading] = useState(false);
  const [validatingCommentId, setValidatingCommentId] = useState<string | null>(null);
  const [validatingRateReviewId, setValidatingRateReviewId] = useState<string | null>(null);
  const [rateReviewSearchText, setRateReviewSearchText] = useState("");
  const [rateReviewDateFrom, setRateReviewDateFrom] = useState("");
  const [rateReviewDateTo, setRateReviewDateTo] = useState("");
  const [rateReviewStatus, setRateReviewStatus] = useState<"false" | "true" | "all">("false");
  const [searchCommentText, setSearchCommentText] = useState("");
  const [searchedComments, setSearchedComments] = useState<AdCommentDto[]>([]);
  const [searchingComments, setSearchingComments] = useState(false);
  const [invalidatingCommentId, setInvalidatingCommentId] = useState<string | null>(null);
  const [targetUserIdentifier, setTargetUserIdentifier] = useState("");
  const [targetRole, setTargetRole] = useState("user");
  const [updatingRole, setUpdatingRole] = useState(false);
  const [userSearchText, setUserSearchText] = useState("");
  const [searchedUsers, setSearchedUsers] = useState<AdminUserSearchResult[]>([]);
  const [searchingUsers, setSearchingUsers] = useState(false);
  const [loadingMoreUsers, setLoadingMoreUsers] = useState(false);
  const [usersNextCursor, setUsersNextCursor] = useState<string | null>(null);
  const [usersLoadFailed, setUsersLoadFailed] = useState(false);
  const usersSentinelRef = useRef<HTMLDivElement | null>(null);
  const usersRequestRef = useRef<AbortController | null>(null);
  const submittedUserQueryRef = useRef("");
  const [usersSort, setUsersSort] = useState<AdminUserSort>("created_at");
  const submittedUsersSortRef = useRef<AdminUserSort>("created_at");
  const [deletingUserId, setDeletingUserId] = useState<string | null>(null);
  const [bookingUserIdToDelete, setBookingUserIdToDelete] = useState("");
  const [deletingUserBookings, setDeletingUserBookings] = useState(false);
  const [countryForm, setCountryForm] = useState<CountryFormState>({
    name: "",
    indicatif: "",
    mask: "",
    code: "",
  });
  const [countries, setCountries] = useState<Country[]>([]);
  const [countriesLoading, setCountriesLoading] = useState(false);
  const [savingCountry, setSavingCountry] = useState(false);
  const [deletingCountryKey, setDeletingCountryKey] = useState<string | null>(null);
  const [editingCountryKey, setEditingCountryKey] = useState<string | null>(null);
  const [geoModalOpen, setGeoModalOpen] = useState(false);
  const [geoCountriesModalOpen, setGeoCountriesModalOpen] = useState(false);
  const [selectedGeoCountry, setSelectedGeoCountry] = useState<DashboardGeoCountryPoint | null>(null);
  const [geoLoading, setGeoLoading] = useState(false);
  const [geoPoints, setGeoPoints] = useState<DashboardGeoPoint[]>([]);
  const [serverMetrics, setServerMetrics] = useState<ServerMetricsPayload | null>(null);
  const [serverMetricsLoading, setServerMetricsLoading] = useState(false);
  const [uploadAudit, setUploadAudit] = useState<UploadAuditPayload | null>(null);
  const [uploadAuditLoading, setUploadAuditLoading] = useState(false);
  const [deletingUploadFilename, setDeletingUploadFilename] = useState<string | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState<DashboardSectionId>("overview");
  const geoMapContainerRef = useRef<HTMLDivElement | null>(null);
  const geoMapRef = useRef<L.Map | null>(null);
  const geoMarkersLayerRef = useRef<L.LayerGroup | null>(null);
  const rateReviewsSentinelRef = useRef<HTMLDivElement | null>(null);

  const normalizedRole = useMemo(() => String(user?.role || "user").toLowerCase(), [user?.role]);
  const canDeleteAds = ["admin", "moderator"].includes(normalizedRole);
  const canManageRoles = normalizedRole === "admin";
  const canOpenServerMetrics = normalizedRole === "admin";
  const canOpenGeoMap = ["admin", "moderator"].includes(normalizedRole);
  const roleBadgeLabel = normalizedRole;
  const serverHistory = useMemo(() => serverMetrics?.history || [], [serverMetrics?.history]);
  const serverSummary = serverMetrics?.summary || null;
  const serverRamSeries = useMemo(() => serverHistory.map((entry) => Number(entry.memory.usedPercent || 0)), [serverHistory]);
  const serverCpuSeries = useMemo(() => serverHistory.map((entry) => Number(entry.cpu.loadPercent || 0)), [serverHistory]);
  const serverRamPoints = useMemo(() => buildLinePoints(serverRamSeries, 560, 200), [serverRamSeries]);
  const serverCpuPoints = useMemo(() => buildLinePoints(serverCpuSeries, 560, 200), [serverCpuSeries]);
  const dashboardMenu = useMemo(
    () =>
      [
        { id: "overview", label: t("dashboard.menuOverview"), icon: GridIcon, visible: true, badge: "" },
        { id: "server", label: t("dashboard.serverHealthTitle"), icon: ActivityIcon, visible: canOpenServerMetrics, badge: "" },
        { id: "delete-ad", label: t("dashboard.deleteAdSectionTitle"), icon: TrashIcon, visible: true, badge: "" },
        { id: "top-ads", label: t("dashboard.topAdsTitle"), icon: CheckSquareIcon, visible: true, badge: "" },
        { id: "ad-ratings", label: t("dashboard.adRatingsTitle"), icon: ChartIcon, visible: canDeleteAds, badge: "" },
        { id: "pending-ads", label: t("dashboard.pendingAdsTitle"), icon: CheckSquareIcon, visible: true, badge: String(pendingAds.length) },
        { id: "comments", label: t("dashboard.commentsModerationTitle"), icon: MessageIcon, visible: true, badge: String(pendingComments.length) },
        { id: "rate-reviews", label: t("dashboard.rateReviewsTitle"), icon: MessageIcon, visible: true, badge: String(rateReviews.filter((item) => item.valide !== true).length) },
        { id: "reports", label: t("dashboard.reportsTitle"), icon: FlagIcon, visible: true, badge: String(ownerReports.length) },
        { id: "search", label: t("dashboard.commentSearchTitle"), icon: SearchIcon, visible: true, badge: "" },
        { id: "user-search", label: t("dashboard.userSearchTitle"), icon: UsersIcon, visible: canManageRoles, badge: "" },
        { id: "roles", label: t("dashboard.userRoleLabel"), icon: UsersIcon, visible: canManageRoles, badge: "" },
        { id: "bookings", label: t("dashboard.bookingDeleteSectionTitle"), icon: ShieldIcon, visible: canManageRoles, badge: "" },
        { id: "countries", label: t("dashboard.countryAddTitle"), icon: GlobeIcon, visible: canManageRoles, badge: "" },
        { id: "geo", label: t("dashboard.geoMapTitle"), icon: GlobeIcon, visible: canOpenGeoMap, badge: "" },
      ].filter((item) => item.visible) as Array<{
        id: DashboardSectionId;
        label: string;
        icon: React.ComponentType<{ "aria-hidden"?: boolean; focusable?: string }>;
        visible: boolean;
        badge: string;
      }>,
    [canDeleteAds, canManageRoles, canOpenGeoMap, canOpenServerMetrics, ownerReports.length, pendingAds.length, pendingComments.length, rateReviews, t]
  );

  useEffect(() => {
    if (dashboardMenu.some((item) => item.id === activeSection)) return;
    setActiveSection(dashboardMenu[0]?.id || "overview");
  }, [activeSection, dashboardMenu]);

  useEffect(() => {
    if (!mobileMenuOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [mobileMenuOpen]);

  const translatedInsights = useMemo(
    () =>
      (serverMetrics?.insights || []).map((insight) => ({
        level: insight.level,
        title: t(`dashboard.insights.${insight.code}.title`, insight.meta || {}),
        description: t(`dashboard.insights.${insight.code}.description`, insight.meta || {}),
      })),
    [serverMetrics?.insights, t]
  );
  const geoCountryPoints = useMemo(() => {
    const grouped = new Map<string, DashboardGeoCountryPoint>();

    geoPoints.forEach((point) => {
      const countryCode = String(point.location.countryCode || "").trim().toUpperCase();
      const country = String(point.location.country || "").trim();
      const key = getCountryGroupingKey(point);
      const weight = getGeoPointWeight(point);
      const existing = grouped.get(key);

      if (!existing) {
        grouped.set(key, {
          key,
          country,
          countryCode,
          latitude: point.location.latitude,
          longitude: point.location.longitude,
          totalVisits: weight,
          sampleUsers: 1,
        });
        return;
      }

      const nextTotal = existing.totalVisits + weight;
      existing.latitude = ((existing.latitude * existing.totalVisits) + (point.location.latitude * weight)) / nextTotal;
      existing.longitude = ((existing.longitude * existing.totalVisits) + (point.location.longitude * weight)) / nextTotal;
      existing.totalVisits = nextTotal;
      existing.sampleUsers += 1;
    });
    return Array.from(grouped.values())
      .map((entry) => {
        const override = entry.countryCode ? COUNTRY_MARKER_CENTER[entry.countryCode] : undefined;
        if (!override) return entry;
        return {
          ...entry,
          latitude: override.latitude,
          longitude: override.longitude,
        };
      })
      .sort((a, b) => b.totalVisits - a.totalVisits);
  }, [geoPoints]);
  const selectedGeoCountryRows = useMemo(() => {
    if (!selectedGeoCountry) return [];
    const grouped = new Map<string, { key: string; city: string; ip: string; visits: number; lastVisitedAt: string | null }>();

    geoPoints.forEach((point) => {
      if (getCountryGroupingKey(point) !== selectedGeoCountry.key) return;
      const city = String(point.location.city || "").trim() || t("dashboard.geoMapUnknownPlace");
      const ip = String(point.location.ip || "").trim() || "-";
      const rowKey = `${city}||${ip}`;
      const existing = grouped.get(rowKey);
      const weight = getGeoPointWeight(point);
      const visitedAtValue = String(point.lastLoginAt || "").trim();
      const visitedAt = visitedAtValue || null;

      if (!existing) {
        grouped.set(rowKey, { key: rowKey, city, ip, visits: weight, lastVisitedAt: visitedAt });
        return;
      }
      existing.visits += weight;
      const existingTime = existing.lastVisitedAt ? new Date(existing.lastVisitedAt).getTime() : Number.NEGATIVE_INFINITY;
      const nextTime = visitedAt ? new Date(visitedAt).getTime() : Number.NEGATIVE_INFINITY;
      if (nextTime > existingTime) {
        existing.lastVisitedAt = visitedAt;
      }
    });

    return Array.from(grouped.values()).sort((a, b) => {
      const aTime = a.lastVisitedAt ? new Date(a.lastVisitedAt).getTime() : Number.NEGATIVE_INFINITY;
      const bTime = b.lastVisitedAt ? new Date(b.lastVisitedAt).getTime() : Number.NEGATIVE_INFINITY;
      if (bTime !== aTime) return bTime - aTime;
      return b.visits - a.visits;
    });
  }, [geoPoints, selectedGeoCountry, t]);

  useEffect(() => {
    if (!geoModalOpen || !geoMapContainerRef.current) return;

    if (!geoMapRef.current) {
      const map = L.map(geoMapContainerRef.current, {
        zoomControl: true,
        attributionControl: true,
        worldCopyJump: true,
      }).setView([20, 0], 2);

      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        maxZoom: 8,
        minZoom: 2,
        attribution: "&copy; OpenStreetMap contributors",
      }).addTo(map);

      geoMapRef.current = map;
      geoMarkersLayerRef.current = L.layerGroup().addTo(map);
    }

    const map = geoMapRef.current;
    const markerLayer = geoMarkersLayerRef.current;
    if (!map || !markerLayer) return;

    markerLayer.clearLayers();
    const maxWeight = geoCountryPoints.reduce((max, point) => Math.max(max, point.totalVisits), 1);
    const bounds: L.LatLngTuple[] = [];

    geoCountryPoints.forEach((point) => {
      const weight = point.totalVisits;
      const color = getGeoPointColor(weight, maxWeight);
      const radius = Math.min(18, 6 + Math.sqrt(weight) * 1.9);
      const latLng: L.LatLngTuple = [point.latitude, point.longitude];
      bounds.push(latLng);

      const marker = L.circleMarker(latLng, {
        radius,
        color: "#ffffff",
        weight: 1.5,
        fillColor: color,
        fillOpacity: 0.9,
      });
      marker.bindTooltip(
        `<strong>${point.country || "Unknown"}</strong><br/>${point.countryCode || "-"}<br/>${point.totalVisits} visite(s) - ${point.sampleUsers} profil(s)`,
        { direction: "top" }
      );
      marker.addTo(markerLayer);
    });

    if (bounds.length === 1) {
      map.setView(bounds[0] as L.LatLngExpression, 4);
    } else if (bounds.length > 1) {
      map.fitBounds(bounds, { padding: [30, 30], maxZoom: 5 });
    } else {
      map.setView([20, 0], 2);
    }

    setTimeout(() => map.invalidateSize(), 0);
  }, [geoModalOpen, geoLoading, geoCountryPoints]);

  useEffect(() => {
    if (geoModalOpen) return;
    if (geoMapRef.current) {
      geoMapRef.current.remove();
      geoMapRef.current = null;
    }
    geoMarkersLayerRef.current = null;
  }, [geoModalOpen]);

  const loadPendingComments = useCallback(async () => {
    setCommentsLoading(true);
    try {
      const response = await listCommentsForModerationApi(false);
      const data = Array.isArray(response?.data?.data) ? response.data.data : [];
      setPendingComments(data);
    } catch {
      setPendingComments([]);
    } finally {
      setCommentsLoading(false);
    }
  }, []);

  const loadRateReviews = useCallback(async ({ cursor, append }: { cursor?: string | null; append: boolean } = { append: false, cursor: null }) => {
    if (append) setRateReviewsLoadingMore(true);
    else setRateReviewsLoading(true);
    try {
      const response = await listRateAdsForModerationApi({
        limit: 20,
        cursor: cursor || null,
        q: rateReviewSearchText.trim(),
        dateFrom: rateReviewDateFrom,
        dateTo: rateReviewDateTo,
        valide: rateReviewStatus === "all" ? "" : rateReviewStatus === "true",
      });
      const data = Array.isArray(response?.data?.data) ? response.data.data : [];
      setRateReviews((prev) => {
        if (!append) return data;
        const byId = new Map<string, RateAdDto>();
        [...prev, ...data].forEach((item) => {
          if (item?.id) byId.set(item.id, item);
        });
        return Array.from(byId.values());
      });
      setRateReviewsHasMore(Boolean(response?.data?.hasMore));
      setRateReviewsNextCursor(response?.data?.nextCursor ?? null);
    } catch (_err) {
      if (!append) setRateReviews([]);
      toast.error(t("dashboard.rateReviewsLoadError"));
    } finally {
      if (append) setRateReviewsLoadingMore(false);
      else setRateReviewsLoading(false);
    }
  }, [rateReviewDateFrom, rateReviewDateTo, rateReviewSearchText, rateReviewStatus, t]);

  const loadOwnerReports = useCallback(async () => {
    setOwnerReportsLoading(true);
    try {
      const response = await listOwnerReportsForModerationApi(120);
      const data = Array.isArray(response?.data?.data) ? response.data.data : [];
      setOwnerReports(data);
    } catch {
      setOwnerReports([]);
      toast.error(t("dashboard.reportsLoadError"));
    } finally {
      setOwnerReportsLoading(false);
    }
  }, [t]);

  const loadServerMetrics = useCallback(async () => {
    if (!canOpenServerMetrics) return;
    setServerMetricsLoading(true);
    try {
      const response = await getAdminServerMetricsApi();
      setServerMetrics(response?.data?.data || null);
    } catch (_err) {
      setServerMetrics(null);
      toast.error(t("dashboard.serverHealthLoadError"));
    } finally {
      setServerMetricsLoading(false);
    }
  }, [canOpenServerMetrics, t]);

  const handleRunUploadAudit = useCallback(async () => {
    setUploadAuditLoading(true);
    try {
      const response = await auditAdminUploadImagesApi();
      setUploadAudit(response?.data?.data || null);
    } catch (_err) {
      setUploadAudit(null);
      toast.error(t("dashboard.uploadAuditError"));
    } finally {
      setUploadAuditLoading(false);
    }
  }, [t]);

  const handleDeleteAuditedImage = useCallback(async (file: { filename: string; relativePath?: string; absolutePath?: string }) => {
    const filename = file?.filename || "";
    const relativePath = file?.relativePath || "";
    const absolutePath = file?.absolutePath || "";
    if (!filename) return;
    setDeletingUploadFilename(filename);
    try {
      const response = await deleteAuditedUploadImageApi({ filename, relativePath, absolutePath });
      const deletedPhotoEntriesCount = Number(response?.data?.data?.deletedPhotoEntriesCount || 0);
      setUploadAudit((prev) => {
        if (!prev) return prev;
        const nextFiles = prev.orphanFiles.filter((file) => file.filename !== filename);
        return {
          ...prev,
          orphanFiles: nextFiles,
          orphanFilesCount: nextFiles.length,
          linkedFilesCount: prev.totalFilesScanned - nextFiles.length,
        };
      });
      toast.success(
        deletedPhotoEntriesCount > 0
          ? t("dashboard.uploadAuditDeleteSuccessWithPhoto", { count: deletedPhotoEntriesCount })
          : t("dashboard.uploadAuditDeleteSuccess")
      );
    } catch (_err) {
      toast.error(t("dashboard.uploadAuditDeleteError"));
    } finally {
      setDeletingUploadFilename(null);
    }
  }, [t]);

  const loadPendingAds = useCallback(async () => {
    setPendingAdsLoading(true);
    try {
      const response = await getPaginatedAdsApi({ status: false, limit: 50 });
      const ads = Array.isArray(response?.data?.data) ? response.data.data : [];
      const locale = (i18n.resolvedLanguage || "fr").split("-")[0];
      setPendingAds(
        ads
          .map((ad: any) => ({
            id: String(ad?.id || ad?._id || ""),
            title: getLocalizedText(ad?.title_i18n, locale, t("dashboard.pendingAdUnknownTitle")),
            address: getLocalizedText(ad?.adress_i18n, locale, ad?.adress || t("dashboard.pendingAdUnknownAddress")),
          }))
          .filter((item) => item.id)
      );
    } catch (_err) {
      setPendingAds([]);
      toast.error(t("dashboard.pendingAdsLoadError"));
    } finally {
      setPendingAdsLoading(false);
    }
  }, [i18n.resolvedLanguage, t]);

  useEffect(() => {
    const query = topAdSearchText.trim();
    if (selectedTopAd && query === selectedTopAd.title) {
      setTopAdSuggestions([]);
      return;
    }
    if (query.length < 2) {
      setTopAdSuggestions([]);
      setTopAdSearching(false);
      return;
    }

    let cancelled = false;
    setTopAdSearching(true);
    const timeoutId = window.setTimeout(async () => {
      try {
        const response = await searchAdsForTopPositionApi(query, 8);
        if (cancelled) return;
        setTopAdSuggestions(Array.isArray(response.data?.data) ? response.data.data : []);
      } catch (_err) {
        if (!cancelled) {
          setTopAdSuggestions([]);
          toast.error(t("dashboard.topAdsSearchError"));
        }
      } finally {
        if (!cancelled) setTopAdSearching(false);
      }
    }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [selectedTopAd, t, topAdSearchText]);

  const handleSelectTopAdSuggestion = useCallback((ad: TopAdSearchItem) => {
    setSelectedTopAd(ad);
    setTopAdSearchText(ad.title);
    setTopAdSuggestions([]);
    setSelectedTopPosition(ad.top || 1);
  }, []);

  const handleSaveTopAdPosition = useCallback(async (event: React.FormEvent) => {
    event.preventDefault();
    if (!selectedTopAd?.id) {
      toast.error(t("dashboard.topAdsSelectRequired"));
      return;
    }

    setTopAdSaving(true);
    try {
      const response = await updateAdTopPositionApi(selectedTopAd.id, selectedTopPosition);
      const updated = response.data?.data;
      if (updated) {
        setSelectedTopAd(updated);
        setTopAdSearchText(updated.title);
        setSelectedTopPosition(updated.top || selectedTopPosition);
      }
      toast.success(t("dashboard.topAdsUpdateSuccess"));
    } catch (_err) {
      toast.error(t("dashboard.topAdsUpdateError"));
    } finally {
      setTopAdSaving(false);
    }
  }, [selectedTopAd?.id, selectedTopPosition, t]);

  const handleClearTopAdPosition = useCallback(async () => {
    if (!selectedTopAd?.id) {
      toast.error(t("dashboard.topAdsSelectRequired"));
      return;
    }

    setTopAdSaving(true);
    try {
      const response = await updateAdTopPositionApi(selectedTopAd.id, null);
      const updated = response.data?.data;
      if (updated) {
        setSelectedTopAd(updated);
        setTopAdSearchText(updated.title);
        setSelectedTopPosition(1);
      }
      toast.success(t("dashboard.topAdsClearSuccess"));
    } catch (_err) {
      toast.error(t("dashboard.topAdsUpdateError"));
    } finally {
      setTopAdSaving(false);
    }
  }, [selectedTopAd?.id, t]);

  useEffect(() => {
    const query = ratingAdSearchText.trim();
    if (selectedRatingAd && query === selectedRatingAd.title) {
      setRatingAdSuggestions([]);
      return;
    }
    if (query.length < 2) {
      setRatingAdSuggestions([]);
      setRatingAdSearching(false);
      return;
    }

    let cancelled = false;
    setRatingAdSearching(true);
    const timeoutId = window.setTimeout(async () => {
      try {
        const response = await searchAdsForTopPositionApi(query, 8);
        if (cancelled) return;
        setRatingAdSuggestions(Array.isArray(response.data?.data) ? response.data.data : []);
      } catch (_err) {
        if (!cancelled) {
          setRatingAdSuggestions([]);
          toast.error(t("dashboard.adRatingsSearchError"));
        }
      } finally {
        if (!cancelled) setRatingAdSearching(false);
      }
    }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [ratingAdSearchText, selectedRatingAd, t]);

  const handleSelectRatingAdSuggestion = useCallback((ad: TopAdSearchItem) => {
    setSelectedRatingAd(ad);
    setRatingAdSearchText(ad.title);
    setRatingAdSuggestions([]);
    setSelectedAdRate(ad.rate || 1);
  }, []);

  const handleSaveAdRating = useCallback(async (event: React.FormEvent) => {
    event.preventDefault();
    if (!selectedRatingAd?.id) {
      toast.error(t("dashboard.adRatingsSelectRequired"));
      return;
    }

    setRatingAdSaving(true);
    try {
      const response = await updateAdRateApi(selectedRatingAd.id, selectedAdRate);
      const updated = response.data?.data;
      if (updated) {
        setSelectedRatingAd(updated);
        setRatingAdSearchText(updated.title);
        setSelectedAdRate(updated.rate || selectedAdRate);
      }
      toast.success(t("dashboard.adRatingsUpdateSuccess"));
    } catch (_err) {
      toast.error(t("dashboard.adRatingsUpdateError"));
    } finally {
      setRatingAdSaving(false);
    }
  }, [selectedAdRate, selectedRatingAd?.id, t]);

  useEffect(() => {
    if (!canDeleteAds) return;
    loadPendingComments();
    void loadRateReviews({ append: false, cursor: null });
    loadPendingAds();
    loadOwnerReports();
  }, [canDeleteAds, loadOwnerReports, loadPendingAds, loadPendingComments, loadRateReviews]);

  useEffect(() => {
    if (activeSection !== "rate-reviews") return;
    void loadRateReviews({ append: false, cursor: null });
  }, [activeSection, loadRateReviews]);

  useEffect(() => {
    if (activeSection !== "rate-reviews") return;
    if (!rateReviewsSentinelRef.current) return;
    if (!rateReviewsHasMore || !rateReviewsNextCursor || rateReviewsLoading || rateReviewsLoadingMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry?.isIntersecting && rateReviewsNextCursor && !rateReviewsLoadingMore) {
          void loadRateReviews({ append: true, cursor: rateReviewsNextCursor });
        }
      },
      { rootMargin: "240px 0px" }
    );
    observer.observe(rateReviewsSentinelRef.current);
    return () => observer.disconnect();
  }, [activeSection, loadRateReviews, rateReviewsHasMore, rateReviewsLoading, rateReviewsLoadingMore, rateReviewsNextCursor]);

  useEffect(() => {
    if (!canOpenServerMetrics) return;
    loadServerMetrics();
  }, [canOpenServerMetrics, loadServerMetrics]);

  const openConfirm = (event: React.FormEvent) => {
    event.preventDefault();
    const trimmed = adId.trim();
    if (!trimmed) {
      toast.error(t("dashboard.idRequired"));
      return;
    }
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    const trimmed = adId.trim();
    if (!trimmed) return;
    setSubmitting(true);
    try {
      await deleteAdCascadeApi(trimmed);
      toast.success(t("dashboard.deleteSuccess"));
      setAdId("");
      setConfirmOpen(false);
    } catch (err: any) {
      const message = err?.response?.data?.message || t("dashboard.deleteError");
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleValidateComment = async (commentId: string) => {
    if (!commentId) return;
    setValidatingCommentId(commentId);
    try {
      await validateCommentByIdApi(commentId, true);
      setPendingComments((prev) => prev.filter((item) => item.id !== commentId));
      toast.success(t("dashboard.commentValidateSuccess"));
    } catch (_err) {
      toast.error(t("dashboard.commentValidateError"));
    } finally {
      setValidatingCommentId(null);
    }
  };

  const handleSearchValidatedComments = async (event: React.FormEvent) => {
    event.preventDefault();
    const query = searchCommentText.trim();
    if (!query) {
      toast.error(t("dashboard.commentSearchRequired"));
      return;
    }
    setSearchingComments(true);
    try {
      const response = await listCommentsForModerationApi(true, query);
      const data = Array.isArray(response?.data?.data) ? response.data.data : [];
      setSearchedComments(data);
    } catch (_err) {
      setSearchedComments([]);
      toast.error(t("dashboard.commentSearchError"));
    } finally {
      setSearchingComments(false);
    }
  };

  const handleInvalidateComment = async (commentId: string) => {
    if (!commentId) return;
    const target = searchedComments.find((item) => item.id === commentId);
    setInvalidatingCommentId(commentId);
    try {
      await validateCommentByIdApi(commentId, false);
      setSearchedComments((prev) => prev.filter((item) => item.id !== commentId));
      if (target) {
        const invalidatedComment: AdCommentDto = { ...target, valide: false };
        setPendingComments((prev) => {
          const withoutDuplicate = prev.filter((item) => item.id !== commentId);
          return [invalidatedComment, ...withoutDuplicate];
        });
      }
      toast.success(t("dashboard.commentInvalidateSuccess"));
    } catch (_err) {
      toast.error(t("dashboard.commentInvalidateError"));
    } finally {
      setInvalidatingCommentId(null);
    }
  };

  const handleUpdateUserRole = async (event: React.FormEvent) => {
    event.preventDefault();
    const trimmedIdentifier = targetUserIdentifier.trim();
    if (!trimmedIdentifier) {
      toast.error(t("dashboard.userRoleIdRequired"));
      return;
    }
    setUpdatingRole(true);
    try {
      await updateUserRoleByIdApi(trimmedIdentifier, targetRole);
      toast.success(t("dashboard.userRoleUpdateSuccess"));
      setTargetUserIdentifier("");
    } catch (err: any) {
      const message = err?.response?.data?.message || t("dashboard.userRoleUpdateError");
      toast.error(message);
    } finally {
      setUpdatingRole(false);
    }
  };

  const handleValidateRateReview = async (reviewId: string, valide = true) => {
    if (!reviewId) return;
    setValidatingRateReviewId(reviewId);
    try {
      await validateRateAdByIdApi(reviewId, valide);
      setRateReviews((prev) => prev.filter((item) => item.id !== reviewId));
      toast.success(valide ? t("dashboard.rateReviewValidateSuccess") : t("dashboard.rateReviewInvalidateSuccess"));
    } catch (_err) {
      toast.error(t("dashboard.rateReviewValidateError"));
    } finally {
      setValidatingRateReviewId(null);
    }
  };

  const loadUsers = useCallback(async (query: string, cursor?: string, sortBy: AdminUserSort = submittedUsersSortRef.current) => {
    if (!canManageRoles || (cursor && usersRequestRef.current)) return;
    usersRequestRef.current?.abort();
    const controller = new AbortController();
    usersRequestRef.current = controller;
    submittedUserQueryRef.current = query;
    submittedUsersSortRef.current = sortBy;
    setUsersLoadFailed(false);
    setSearchingUsers(!cursor);
    setLoadingMoreUsers(Boolean(cursor));
    if (!cursor) {
      setSearchedUsers([]);
      setUsersNextCursor(null);
    }
    try {
      const response = await searchAdminUsersApi(query, 20, cursor, controller.signal, sortBy);
      if (controller.signal.aborted) return;
      const data = Array.isArray(response?.data?.data) ? response.data.data : [];
      setSearchedUsers((previous) => cursor
        ? [...previous, ...data.filter((entry) => !previous.some((user) => user.id === entry.id))]
        : data);
      setUsersNextCursor(response.data.hasMore ? response.data.nextCursor : null);
    } catch (err: any) {
      if (controller.signal.aborted) return;
      setUsersLoadFailed(true);
      const message = err?.response?.data?.message || t("dashboard.userSearchError");
      toast.error(message);
    } finally {
      if (usersRequestRef.current === controller) {
        usersRequestRef.current = null;
        setSearchingUsers(false);
        setLoadingMoreUsers(false);
      }
    }
  }, [canManageRoles, t]);

  useEffect(() => {
    if (activeSection !== "user-search" || !canManageRoles) return;
    setUserSearchText("");
    setUsersSort("created_at");
    void loadUsers("", undefined, "created_at");
    return () => {
      usersRequestRef.current?.abort();
      usersRequestRef.current = null;
    };
  }, [activeSection, canManageRoles, loadUsers]);

  useEffect(() => {
    if (activeSection !== "user-search" || !canManageRoles || !usersNextCursor ||
        searchingUsers || loadingMoreUsers || usersLoadFailed || !usersSentinelRef.current) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry?.isIntersecting) void loadUsers(submittedUserQueryRef.current, usersNextCursor);
    }, { rootMargin: "240px 0px" });
    observer.observe(usersSentinelRef.current);
    return () => observer.disconnect();
  }, [activeSection, canManageRoles, usersNextCursor, searchingUsers, loadingMoreUsers, usersLoadFailed, loadUsers]);

  const handleSearchUsers = (event: React.FormEvent) => {
    event.preventDefault();
    const query = userSearchText.trim();
    if (query.length > 0 && query.length < 2) {
      toast.error(t("dashboard.userSearchRequired"));
      return;
    }
    void loadUsers(query);
  };

  const handleDeleteUserCascade = async (userToDelete: AdminUserSearchResult) => {
    if (!userToDelete?.id || deletingUserId) return;
    const displayName = [userToDelete.lastName, userToDelete.firstName].filter(Boolean).join(" ").trim() || userToDelete.email || userToDelete.id;
    const confirmed = window.confirm(t("dashboard.userDeleteConfirm", { name: displayName }));
    if (!confirmed) return;

    setDeletingUserId(userToDelete.id);
    try {
      await deleteAdminUserCascadeApi(userToDelete.id);
      setSearchedUsers((prev) => prev.filter((item) => item.id !== userToDelete.id));
      toast.success(t("dashboard.userDeleteSuccess"));
    } catch (err: any) {
      const message = err?.response?.data?.message || t("dashboard.userDeleteError");
      toast.error(message);
    } finally {
      setDeletingUserId(null);
    }
  };

  const handleValidateAdConfirm = async () => {
    if (!pendingAdToValidate?.id) return;
    setValidatingAd(true);
    try {
      await updateAdStatusApi(pendingAdToValidate.id, true);
      setPendingAds((prev) => prev.filter((item) => item.id !== pendingAdToValidate.id));
      toast.success(t("dashboard.pendingAdValidateSuccess"));
      setPendingAdToValidate(null);
    } catch (_err) {
      toast.error(t("dashboard.pendingAdValidateError"));
    } finally {
      setValidatingAd(false);
    }
  };

  const handleDeleteUserBookings = async (event: React.FormEvent) => {
    event.preventDefault();
    const trimmed = bookingUserIdToDelete.trim();
    if (!trimmed) {
      toast.error(t("dashboard.bookingDeleteUserIdRequired"));
      return;
    }
    setDeletingUserBookings(true);
    try {
      const response = await deleteBookingsByUserIdApi({ id_user: trimmed });
      const deletedCount = Number(response?.data?.deletedCount || 0);
      toast.success(t("dashboard.bookingDeleteSuccess", { count: deletedCount }));
      setBookingUserIdToDelete("");
    } catch (err: any) {
      const message = err?.response?.data?.message || t("dashboard.bookingDeleteError");
      toast.error(message);
    } finally {
      setDeletingUserBookings(false);
    }
  };

  const handleCountryFormChange = (field: keyof CountryFormState, value: string) => {
    setCountryForm((prev) => ({
      ...prev,
      [field]: field === "code" ? value.toUpperCase() : value,
    }));
  };

  const loadCountries = useCallback(async () => {
    setCountriesLoading(true);
    try {
      const response = await getCountriesApi();
      const data = Array.isArray(response?.data) ? response.data : [];
      setCountries(data);
    } catch (_err) {
      setCountries([]);
      toast.error(t("dashboard.countryListError"));
    } finally {
      setCountriesLoading(false);
    }
  }, [t]);

  const resetCountryForm = () => {
    setCountryForm({ name: "", indicatif: "", mask: "", code: "" });
    setEditingCountryKey(null);
  };

  const handleSaveCountry = async (event: React.FormEvent) => {
    event.preventDefault();
    const payload: Country = {
      name: countryForm.name.trim(),
      indicatif: countryForm.indicatif.trim(),
      mask: countryForm.mask.trim(),
      code: countryForm.code.trim().toUpperCase(),
    };

    if (!payload.name || !payload.indicatif || !payload.mask || !payload.code) {
      toast.error(t("dashboard.countryAddRequired"));
      return;
    }

    setSavingCountry(true);
    try {
      if (editingCountryKey) {
        await putCountryApi(editingCountryKey, payload);
        toast.success(t("dashboard.countryUpdateSuccess"));
      } else {
        await postCountryApi(payload);
        toast.success(t("dashboard.countryAddSuccess"));
      }
      resetCountryForm();
      await loadCountries();
    } catch (err: any) {
      const message = err?.response?.data?.message || (
        editingCountryKey ? t("dashboard.countryUpdateError") : t("dashboard.countryAddError")
      );
      toast.error(message);
    } finally {
      setSavingCountry(false);
    }
  };

  const handleEditCountry = (country: Country) => {
    const key = String(country.id || country._id || country.code || "").trim();
    if (!key) return;
    setEditingCountryKey(key);
    setCountryForm({
      name: getCountryDisplayName(country),
      indicatif: country.indicatif || "",
      mask: country.mask || "",
      code: country.code || "",
    });
  };

  const handleDeleteCountry = async (country: Country) => {
    const key = String(country.id || country._id || country.code || "").trim();
    if (!key) return;
    setDeletingCountryKey(key);
    try {
      await deleteCountryApi(key);
      setCountries((prev) => prev.filter((item) => String(item.id || item._id || item.code || "") !== key));
      if (editingCountryKey === key) resetCountryForm();
      toast.success(t("dashboard.countryDeleteSuccess"));
    } catch (err: any) {
      const message = err?.response?.data?.message || t("dashboard.countryDeleteError");
      toast.error(message);
    } finally {
      setDeletingCountryKey(null);
    }
  };

  useEffect(() => {
    if (activeSection !== "countries") return;
    loadCountries();
  }, [activeSection, loadCountries]);

  const loadGeoPoints = useCallback(async () => {
    setGeoLoading(true);
    try {
      const response = await getAdminUserLoginLocationsApi();
      const data = Array.isArray(response?.data?.data) ? response.data.data : [];
      setGeoPoints(
        data.filter((entry: any) =>
          Number.isFinite(Number(entry?.location?.latitude)) &&
          Number.isFinite(Number(entry?.location?.longitude))
        )
      );
    } catch (_err) {
      setGeoPoints([]);
      toast.error(t("dashboard.geoMapLoadError"));
    } finally {
      setGeoLoading(false);
    }
  }, [t]);

  const handleOpenGeoModal = async () => {
    setGeoModalOpen(true);
    await loadGeoPoints();
  };

  const handleCloseGeoModal = () => {
    setGeoModalOpen(false);
    setGeoCountriesModalOpen(false);
    setSelectedGeoCountry(null);
  };

  const handleSelectSection = (sectionId: DashboardSectionId) => {
    setActiveSection(sectionId);
    setMobileMenuOpen(false);
  };

  if (!user) {
    return (
      <div className={styles.page}>
        <div className={styles.shell}>
          <p className={styles.state}>{t("dashboard.loginRequired")}</p>
        </div>
      </div>
    );
  }

  if (!canDeleteAds) {
    return (
      <div className={styles.page}>
        <div className={styles.shell}>
          <p className={styles.state}>{t("dashboard.notAllowed")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <section className={styles.shell}>
        <div className={styles.dashboardLayout}>
          <aside className={`${styles.sidebar} ${mobileMenuOpen ? styles.sidebarMobileOpen : ""}`}>
            <button
              type="button"
              className={styles.mobileMenuClose}
              onClick={() => setMobileMenuOpen(false)}
              aria-label={t("dashboard.mobileMenuClose")}
            >
              <CloseIcon />
            </button>
            <div className={styles.sidebarMeta}>
              <span>{t("profileInfo.roleLabel")}</span>
              <strong>{roleBadgeLabel}</strong>
            </div>
            <nav className={styles.sidebarNav} aria-label={t("dashboard.menuTitle")}>
              {dashboardMenu.map((item) => {
                const Icon = item.icon;
                return (
                  <button
                    key={item.id}
                    type="button"
                    className={`${styles.sidebarLink} ${activeSection === item.id ? styles.sidebarLinkActive : ""}`}
                    onClick={() => handleSelectSection(item.id)}
                  >
                    <span className={styles.sidebarLinkIcon}>
                      <Icon />
                    </span>
                    <span className={styles.sidebarLinkLabel}>{item.label}</span>
                    {item.badge && item.badge !== "0" ? <span className={styles.sidebarBadge}>{item.badge}</span> : null}
                  </button>
                );
              })}
            </nav>
          </aside>

          <div className={styles.workspace}>
            <div className={styles.mobileMenuBar}>
              <button
                type="button"
                className={styles.mobileMenuToggle}
                onClick={() => setMobileMenuOpen(true)}
                aria-label={t("dashboard.mobileMenuOpen")}
              >
                <MenuIcon />
                <span>{t("dashboard.mobileMenuLabel")}</span>
              </button>
            </div>
            <header className={styles.workspaceHeader}>
              <div className={styles.headerMain}>
                <p className={styles.eyebrow}>{t("dashboard.menuOverview")}</p>
                <h1>{t("dashboard.title")}</h1>
                <p className={styles.subtitle}>{t("dashboard.subtitle")}</p>
              </div>
              <div className={styles.headerMetrics}>
                <article className={styles.metricCard}>
                  <span>{t("dashboard.pendingAdsTitle")}</span>
                  <strong>{pendingAdsLoading ? "..." : pendingAds.length}</strong>
                </article>
                <article className={styles.metricCard}>
                  <span>{t("dashboard.commentsModerationTitle")}</span>
                  <strong>{commentsLoading ? "..." : pendingComments.length}</strong>
                </article>
                <article className={styles.metricCard}>
                  <span>{t("dashboard.reportsTitle")}</span>
                  <strong>{ownerReportsLoading ? "..." : ownerReports.length}</strong>
                </article>
              </div>
            </header>

            <section className={styles.contentPanel}>
              {activeSection === "overview" ? (
                <div className={styles.overviewStack}>
                  <div className={styles.overviewGrid}>
                    <article className={styles.overviewCard}>
                      <span>{t("dashboard.pendingAdsTitle")}</span>
                      <strong>{pendingAds.length}</strong>
                      <button type="button" onClick={() => setActiveSection("pending-ads")}>
                        {t("dashboard.pendingAdsRefresh")}
                      </button>
                    </article>
                    <article className={styles.overviewCard}>
                      <span>{t("dashboard.commentsModerationTitle")}</span>
                      <strong>{pendingComments.length}</strong>
                      <button type="button" onClick={() => setActiveSection("comments")}>
                        {t("dashboard.commentsRefresh")}
                      </button>
                    </article>
                    <article className={styles.overviewCard}>
                      <span>{t("dashboard.reportsTitle")}</span>
                      <strong>{ownerReports.length}</strong>
                      <button type="button" onClick={() => setActiveSection("reports")}>
                        {t("dashboard.reportsRefresh")}
                      </button>
                    </article>
                  </div>
                  <div className={styles.shortcutGrid}>
                    {canOpenServerMetrics ? (
                      <button type="button" className={styles.shortcutCard} onClick={() => setActiveSection("server")}>
                        <ActivityIcon />
                        <strong>{t("dashboard.serverHealthTitle")}</strong>
                        <span>{t("dashboard.serverHealthSubtitle")}</span>
                      </button>
                    ) : null}
                    <button type="button" className={styles.shortcutCard} onClick={() => setActiveSection("delete-ad")}>
                      <TrashIcon />
                      <strong>{t("dashboard.deleteAdSectionTitle")}</strong>
                      <span>{t("dashboard.subtitle")}</span>
                    </button>
                    <button type="button" className={styles.shortcutCard} onClick={() => setActiveSection("top-ads")}>
                      <CheckSquareIcon />
                      <strong>{t("dashboard.topAdsTitle")}</strong>
                      <span>{t("dashboard.topAdsSubtitle")}</span>
                    </button>
                    <button type="button" className={styles.shortcutCard} onClick={() => setActiveSection("search")}>
                      <SearchIcon />
                      <strong>{t("dashboard.commentSearchTitle")}</strong>
                      <span>{t("dashboard.commentSearchPlaceholder")}</span>
                    </button>
                    {canManageRoles ? (
                      <button type="button" className={styles.shortcutCard} onClick={() => setActiveSection("countries")}>
                        <GlobeIcon />
                        <strong>{t("dashboard.countryAddTitle")}</strong>
                        <span>{t("dashboard.countryAddSubmit")}</span>
                      </button>
                    ) : null}
                    {canManageRoles ? (
                      <button type="button" className={styles.shortcutCard} onClick={() => setActiveSection("user-search")}>
                        <UsersIcon />
                        <strong>{t("dashboard.userSearchTitle")}</strong>
                        <span>{t("dashboard.userSearchPlaceholder")}</span>
                      </button>
                    ) : null}
                    {canManageRoles ? (
                      <button type="button" className={styles.shortcutCard} onClick={() => setActiveSection("roles")}>
                        <UsersIcon />
                        <strong>{t("dashboard.userRoleLabel")}</strong>
                        <span>{t("dashboard.userRoleSubmit")}</span>
                      </button>
                    ) : null}
                    {canOpenGeoMap ? (
                      <button type="button" className={styles.shortcutCard} onClick={() => setActiveSection("geo")}>
                        <GlobeIcon />
                        <strong>{t("dashboard.geoMapTitle")}</strong>
                        <span>{t("dashboard.geoMapDescription")}</span>
                      </button>
                    ) : null}
                  </div>
                </div>
              ) : null}

              {activeSection === "server" && canOpenServerMetrics ? (
                <section className={styles.serverSection}>
                  <div className={styles.serverSectionHeader}>
                    <div>
                      <h2>{t("dashboard.serverHealthTitle")}</h2>
                      <p>{t("dashboard.serverHealthSubtitle")}</p>
                    </div>
                    <button type="button" onClick={loadServerMetrics} disabled={serverMetricsLoading}>
                      {serverMetricsLoading ? t("dashboard.serverHealthLoading") : t("dashboard.serverHealthRefresh")}
                    </button>
                  </div>

                  {serverMetricsLoading && !serverSummary ? (
                    <p className={styles.state}>{t("dashboard.serverHealthLoading")}</p>
                  ) : null}

                  {!serverMetricsLoading && !serverSummary ? (
                    <p className={styles.state}>{t("dashboard.serverHealthEmpty")}</p>
                  ) : null}

                  {serverSummary ? (
                    <>
                      <div className={styles.serverHeroGrid}>
                        <article className={styles.serverStatCard}>
                          <div className={styles.serverStatTop}>
                            <span className={styles.serverStatIcon}><HardDriveIcon /></span>
                            <div>
                              <span>{t("dashboard.serverDiskUsageTitle")}</span>
                              <strong>{formatPercent(serverSummary.disk.usedPercent, dashboardLang)}</strong>
                            </div>
                          </div>
                          <p>{t("dashboard.serverDiskUsageHint", {
                            uploadSize: formatBytes(serverSummary.disk.uploadBytes, dashboardLang),
                            freeSize: formatBytes(serverSummary.disk.freeBytes, dashboardLang),
                          })}</p>
                        </article>

                        <article className={styles.serverStatCard}>
                          <div className={styles.serverStatTop}>
                            <span className={styles.serverStatIcon}><PieChartIcon /></span>
                            <div>
                              <span>{t("dashboard.serverUploadGrowthTitle")}</span>
                              <strong>{formatBytes(serverSummary.disk.uploadGrowth24hBytes, dashboardLang)}</strong>
                            </div>
                          </div>
                          <p>
                            {serverSummary.disk.estimatedDaysUntilFull !== null
                              ? t("dashboard.serverDaysUntilFull", { days: serverSummary.disk.estimatedDaysUntilFull })
                              : t("dashboard.serverGrowthStable")}
                          </p>
                        </article>

                        <article className={styles.serverStatCard}>
                          <div className={styles.serverStatTop}>
                            <span className={styles.serverStatIcon}><CpuIcon /></span>
                            <div>
                              <span>{t("dashboard.serverMemoryTitle")}</span>
                              <strong>{formatPercent(serverSummary.memory.usedPercent, dashboardLang)}</strong>
                            </div>
                          </div>
                          <p>{t("dashboard.serverMemoryHint", {
                            peak: formatPercent(serverSummary.memory.peakPercent24h, dashboardLang),
                            used: formatBytes(serverSummary.memory.usedBytes, dashboardLang),
                          })}</p>
                        </article>

                        <article className={styles.serverStatCard}>
                          <div className={styles.serverStatTop}>
                            <span className={styles.serverStatIcon}><ActivityIcon /></span>
                            <div>
                              <span>{t("dashboard.serverCpuTitle")}</span>
                              <strong>{formatPercent(serverSummary.cpu.currentPercent, dashboardLang)}</strong>
                            </div>
                          </div>
                          <p>{t("dashboard.serverCpuHint", {
                            peak: formatPercent(serverSummary.cpu.peakPercent24h, dashboardLang),
                            cores: serverSummary.cpu.cores,
                          })}</p>
                        </article>
                      </div>

                      <div className={styles.serverChartsGrid}>
                        <article className={styles.serverChartCard}>
                          <header className={styles.serverChartHeader}>
                            <div>
                              <h3>{t("dashboard.serverMemoryChartTitle")}</h3>
                              <p>{t("dashboard.serverChartWindow", {
                                hours: serverMetrics?.windowHours || 24,
                                interval: serverMetrics?.sampleIntervalMinutes || 15,
                              })}</p>
                            </div>
                            <strong>{formatPercent(serverSummary.memory.avgPercent24h, dashboardLang)}</strong>
                          </header>
                          <svg viewBox="0 0 560 200" className={styles.serverChartSvg} role="img" aria-label={t("dashboard.serverMemoryChartTitle")}>
                            <polyline className={styles.serverChartGridLine} points="12,188 548,188" />
                            <polyline className={styles.serverChartGridLine} points="12,100 548,100" />
                            <polyline className={styles.serverChartLineMemory} points={serverRamPoints} />
                          </svg>
                        </article>

                        <article className={styles.serverChartCard}>
                          <header className={styles.serverChartHeader}>
                            <div>
                              <h3>{t("dashboard.serverCpuChartTitle")}</h3>
                              <p>{t("dashboard.serverProcessHint", {
                                uptime: formatDuration(serverSummary.process.uptimeSeconds, dashboardLang),
                                rss: formatBytes(serverSummary.process.rssBytes, dashboardLang),
                              })}</p>
                            </div>
                            <strong>{formatPercent(serverSummary.cpu.peakPercent24h, dashboardLang)}</strong>
                          </header>
                          <svg viewBox="0 0 560 200" className={styles.serverChartSvg} role="img" aria-label={t("dashboard.serverCpuChartTitle")}>
                            <polyline className={styles.serverChartGridLine} points="12,188 548,188" />
                            <polyline className={styles.serverChartGridLine} points="12,100 548,100" />
                            <polyline className={styles.serverChartLineCpu} points={serverCpuPoints} />
                          </svg>
                        </article>
                      </div>

                      <div className={styles.serverBottomGrid}>
                        <article className={styles.serverGaugeCard}>
                          <header className={styles.serverGaugeHeader}>
                            <h3>{t("dashboard.serverUploadFolderTitle")}</h3>
                            <span>{serverMetrics?.current?.disk.uploadDir || "-"}</span>
                          </header>
                          <div className={styles.serverGaugeRow}>
                            <div
                              className={styles.serverGauge}
                              style={{ ["--gauge-value" as any]: `${Math.max(0, Math.min(100, serverSummary.disk.usedPercent))}%` }}
                            >
                              <div className={styles.serverGaugeInner}>
                                <strong>{formatPercent(serverSummary.disk.usedPercent, dashboardLang)}</strong>
                                <span>{t("dashboard.serverDiskGaugeLabel")}</span>
                              </div>
                            </div>
                            <div className={styles.serverGaugeMeta}>
                              <p><strong>{formatBytes(serverSummary.disk.uploadBytes, dashboardLang)}</strong> {t("dashboard.serverUploadStored")}</p>
                              <p><strong>{serverSummary.disk.uploadFiles}</strong> {t("dashboard.serverUploadFiles")}</p>
                              <p><strong>{formatBytes(serverSummary.disk.freeBytes, dashboardLang)}</strong> {t("dashboard.serverDiskFree")}</p>
                            </div>
                          </div>
                        </article>

                      </div>

                      <article className={styles.serverInsightsCard}>
                        <header className={styles.serverGaugeHeader}>
                          <div>
                            <h3>{t("dashboard.uploadAuditTitle")}</h3>
                            <span>{t("dashboard.uploadAuditSubtitle")}</span>
                          </div>
                          <button type="button" onClick={handleRunUploadAudit} disabled={uploadAuditLoading}>
                            {uploadAuditLoading ? t("dashboard.uploadAuditLoading") : t("dashboard.uploadAuditRun")}
                          </button>
                        </header>

                        {uploadAuditLoading ? (
                          <div className={styles.uploadAuditLoader}>
                            <div className={styles.uploadAuditSpinner} aria-hidden="true" />
                            <p>{t("dashboard.uploadAuditLoading")}</p>
                          </div>
                        ) : null}

                        {!uploadAuditLoading && uploadAudit ? (
                          <div className={styles.uploadAuditResults}>
                            <div className={styles.uploadAuditSummary}>
                              <p><strong>{uploadAudit.totalFilesScanned}</strong> {t("dashboard.uploadAuditScanned")}</p>
                              <p><strong>{uploadAudit.linkedFilesCount}</strong> {t("dashboard.uploadAuditLinked")}</p>
                              <p><strong>{uploadAudit.orphanFilesCount}</strong> {t("dashboard.uploadAuditOrphan")}</p>
                            </div>
                            <p className={styles.uploadAuditMeta}>
                              {t("dashboard.uploadAuditDir", { dir: uploadAudit.uploadDir })}
                            </p>
                            <p className={styles.uploadAuditMeta}>
                              {t("dashboard.uploadAuditScannedAt", { date: formatDateTime(uploadAudit.scannedAt, dashboardLang) })}
                            </p>

                            {uploadAudit.orphanFilesCount === 0 ? (
                              <p className={styles.state}>{t("dashboard.uploadAuditEmpty")}</p>
                            ) : (
                              <div className={styles.uploadAuditList}>
                                {uploadAudit.orphanFiles.map((file) => (
                                  <article key={file.absolutePath} className={styles.uploadAuditItem}>
                                    <div className={styles.uploadAuditItemHead}>
                                      <strong>{file.filename}</strong>
                                      <button
                                        type="button"
                                        className={styles.uploadAuditDeleteBtn}
                                        onClick={() => handleDeleteAuditedImage(file)}
                                        disabled={deletingUploadFilename === file.filename}
                                        aria-label={t("dashboard.uploadAuditDelete")}
                                        title={t("dashboard.uploadAuditDelete")}
                                      >
                                        <RemoveIcon />
                                      </button>
                                    </div>
                                    <span>{file.relativePath}</span>
                                    <span>{formatBytes(file.sizeBytes, dashboardLang)}</span>
                                    <span>{formatDateTime(file.updatedAt, dashboardLang)}</span>
                                    {file.matchedPhotos.length > 0 ? (
                                      <span>{t("dashboard.uploadAuditMatchedPhotos", { count: file.matchedPhotos.length })}</span>
                                    ) : null}
                                  </article>
                                ))}
                              </div>
                            )}
                          </div>
                        ) : null}
                      </article>

                      <article className={styles.serverInsightsCard}>
                        <header className={styles.serverGaugeHeader}>
                          <h3>{t("dashboard.serverInsightsTitle")}</h3>
                          <span>{t("dashboard.serverInsightsSubtitle")}</span>
                        </header>
                        <div className={styles.serverInsightsList}>
                          {translatedInsights.map((insight, index) => (
                            <article key={`${insight.title}-${index}`} className={`${styles.serverInsightItem} ${styles[`serverInsight${insight.level.charAt(0).toUpperCase()}${insight.level.slice(1)}`]}`}>
                              <strong>{insight.title}</strong>
                              <p>{insight.description}</p>
                            </article>
                          ))}
                        </div>
                      </article>
                    </>
                  ) : null}
                </section>
              ) : null}

              {activeSection === "delete-ad" ? (
                <section className={`${styles.toolCard} ${styles.formDanger}`}>
                  <div className={styles.toolHeader}>
                    <h2>{t("dashboard.deleteAdSectionTitle")}</h2>
                  </div>
                  <form className={styles.form} onSubmit={openConfirm}>
                    <label htmlFor="dashboard-ad-id">{t("dashboard.adIdLabel")}</label>
                    <input id="dashboard-ad-id" type="text" value={adId} onChange={(event) => setAdId(event.target.value)} placeholder={t("dashboard.adIdPlaceholder")} />
                    <button type="submit">{t("dashboard.submit")}</button>
                  </form>
                </section>
              ) : null}

              {activeSection === "top-ads" ? (
                <section className={`${styles.toolCard} ${styles.formRole}`}>
                  <div className={styles.toolHeader}>
                    <h2>{t("dashboard.topAdsTitle")}</h2>
                  </div>
                  <form
                    className={`${styles.form} ${styles.topAdsForm} ${topAdSuggestions.length > 0 ? styles.topAdsFormWithSuggestions : ""}`}
                    onSubmit={handleSaveTopAdPosition}
                  >
                    <div className={styles.topAdsSearchField}>
                      <label htmlFor="dashboard-top-ad-search">{t("dashboard.topAdsSearchLabel")}</label>
                      <input
                        id="dashboard-top-ad-search"
                        type="search"
                        value={topAdSearchText}
                        onChange={(event) => {
                          setTopAdSearchText(event.target.value);
                          setSelectedTopAd(null);
                        }}
                        placeholder={t("dashboard.topAdsSearchPlaceholder")}
                        autoComplete="off"
                        disabled={topAdSaving}
                      />
                      {topAdSearching ? <span className={styles.topAdsSearchState}>{t("dashboard.topAdsSearching")}</span> : null}
                      {topAdSuggestions.length > 0 ? (
                        <div className={styles.topAdsSuggestions} role="listbox">
                          {topAdSuggestions.map((ad) => (
                            <button
                              key={ad.id}
                              type="button"
                              className={styles.topAdsSuggestion}
                              onClick={() => handleSelectTopAdSuggestion(ad)}
                              disabled={topAdSaving}
                            >
                              <span>{ad.title}</span>
                              <strong>{ad.top ? t("dashboard.topAdsCurrentPosition", { position: ad.top }) : t("dashboard.topAdsNoPosition")}</strong>
                            </button>
                          ))}
                        </div>
                      ) : null}
                    </div>
                    <div className={styles.topAdsPositionField}>
                      <label htmlFor="dashboard-top-ad-position">{t("dashboard.topAdsPositionLabel")}</label>
                      <select
                        id="dashboard-top-ad-position"
                        value={selectedTopPosition}
                        onChange={(event) => setSelectedTopPosition(Number(event.target.value))}
                        disabled={topAdSaving}
                      >
                        {[1, 2, 3, 4, 5].map((position) => (
                          <option key={position} value={position}>{position}</option>
                        ))}
                      </select>
                    </div>
                    <div className={styles.topAdsActions}>
                      <button type="submit" disabled={topAdSaving || !selectedTopAd}>
                        {topAdSaving ? t("dashboard.topAdsSaving") : t("dashboard.topAdsSubmit")}
                      </button>
                      <button type="button" className={styles.secondaryButton} onClick={handleClearTopAdPosition} disabled={topAdSaving || !selectedTopAd}>
                        {t("dashboard.topAdsClear")}
                      </button>
                    </div>
                  </form>
                  {selectedTopAd ? (
                    <article className={styles.topAdsSelectedCard}>
                      <span>{t("dashboard.topAdsSelected")}</span>
                      <strong>{selectedTopAd.title}</strong>
                      <p>{selectedTopAd.top ? t("dashboard.topAdsCurrentPosition", { position: selectedTopAd.top }) : t("dashboard.topAdsNoPosition")}</p>
                    </article>
                  ) : null}
                </section>
              ) : null}

              {activeSection === "ad-ratings" && canDeleteAds ? (
                <section className={`${styles.toolCard} ${styles.formRole}`}>
                  <div className={styles.toolHeader}>
                    <h2>{t("dashboard.adRatingsTitle")}</h2>
                  </div>
                  <form
                    className={`${styles.form} ${styles.topAdsForm} ${ratingAdSuggestions.length > 0 ? styles.topAdsFormWithSuggestions : ""}`}
                    onSubmit={handleSaveAdRating}
                  >
                    <div className={styles.topAdsSearchField}>
                      <label htmlFor="dashboard-rating-ad-search">{t("dashboard.adRatingsSearchLabel")}</label>
                      <input
                        id="dashboard-rating-ad-search"
                        type="search"
                        value={ratingAdSearchText}
                        onChange={(event) => {
                          setRatingAdSearchText(event.target.value);
                          setSelectedRatingAd(null);
                        }}
                        placeholder={t("dashboard.adRatingsSearchPlaceholder")}
                        autoComplete="off"
                        disabled={ratingAdSaving}
                      />
                      {ratingAdSearching ? <span className={styles.topAdsSearchState}>{t("dashboard.adRatingsSearching")}</span> : null}
                      {ratingAdSuggestions.length > 0 ? (
                        <div className={styles.topAdsSuggestions} role="listbox">
                          {ratingAdSuggestions.map((ad) => (
                            <button
                              key={ad.id}
                              type="button"
                              className={styles.topAdsSuggestion}
                              onClick={() => handleSelectRatingAdSuggestion(ad)}
                              disabled={ratingAdSaving}
                            >
                              <span>{ad.title}</span>
                              <strong>{ad.rate ? t("dashboard.adRatingsCurrentRate", { rate: ad.rate }) : t("dashboard.adRatingsNoRate")}</strong>
                            </button>
                          ))}
                        </div>
                      ) : null}
                    </div>
                    <div className={styles.topAdsPositionField}>
                      <label htmlFor="dashboard-ad-rating">{t("dashboard.adRatingsRateLabel")}</label>
                      <select
                        id="dashboard-ad-rating"
                        value={selectedAdRate}
                        onChange={(event) => setSelectedAdRate(Number(event.target.value))}
                        disabled={ratingAdSaving}
                      >
                        {[1, 2, 3, 4, 5].map((rate) => (
                          <option key={rate} value={rate}>{rate}</option>
                        ))}
                      </select>
                    </div>
                    <div className={styles.topAdsActions}>
                      <button type="submit" disabled={ratingAdSaving || !selectedRatingAd}>
                        {ratingAdSaving ? t("dashboard.adRatingsSaving") : t("dashboard.adRatingsSubmit")}
                      </button>
                    </div>
                  </form>
                  {selectedRatingAd ? (
                    <article className={styles.topAdsSelectedCard}>
                      <span>{t("dashboard.adRatingsSelected")}</span>
                      <strong>{selectedRatingAd.title}</strong>
                      <p>{selectedRatingAd.rate ? t("dashboard.adRatingsCurrentRate", { rate: selectedRatingAd.rate }) : t("dashboard.adRatingsNoRate")}</p>
                    </article>
                  ) : null}
                </section>
              ) : null}

              {activeSection === "roles" && canManageRoles ? (
                <section className={`${styles.toolCard} ${styles.formRole}`}>
                  <div className={styles.toolHeader}>
                    <h2>{t("dashboard.userRoleLabel")}</h2>
                  </div>
                  <form className={styles.form} onSubmit={handleUpdateUserRole}>
                    <label htmlFor="dashboard-user-id">{t("dashboard.userRoleIdLabel")}</label>
                    <input id="dashboard-user-id" type="text" value={targetUserIdentifier} onChange={(event) => setTargetUserIdentifier(event.target.value)} placeholder={t("dashboard.userRoleIdPlaceholder")} />
                    <label htmlFor="dashboard-user-role">{t("dashboard.userRoleLabel")}</label>
                    <select id="dashboard-user-role" value={targetRole} onChange={(event) => setTargetRole(event.target.value)} disabled={updatingRole}>
                      <option value="user">{t("dashboard.userRoleUser")}</option>
                      <option value="moderator">{t("dashboard.userRoleModerator")}</option>
                      <option value="admin">{t("dashboard.userRoleAdmin")}</option>
                    </select>
                    <button type="submit" disabled={updatingRole}>
                      {updatingRole ? t("dashboard.userRoleUpdating") : t("dashboard.userRoleSubmit")}
                    </button>
                  </form>
                </section>
              ) : null}

              {activeSection === "user-search" && canManageRoles ? (
                <section className={`${styles.toolCard} ${styles.formRole}`}>
                  <div className={styles.toolHeader}>
                    <h2>{t("dashboard.userSearchTitle")}</h2>
                  </div>
                  <form className={styles.form} onSubmit={handleSearchUsers}>
                    <label htmlFor="dashboard-user-search">{t("dashboard.userSearchLabel")}</label>
                    <input id="dashboard-user-search" type="text" value={userSearchText} onChange={(event) => setUserSearchText(event.target.value)} placeholder={t("dashboard.userSearchPlaceholder")} disabled={searchingUsers} />
                    <label htmlFor="dashboard-user-sort">{t("dashboard.userSortLabel")}</label>
                    <select id="dashboard-user-sort" value={usersSort} onChange={(event) => {
                      const sortBy = event.target.value as AdminUserSort;
                      setUsersSort(sortBy);
                      void loadUsers(submittedUserQueryRef.current, undefined, sortBy);
                    }}>
                      <option value="created_at">{t("dashboard.userSortRegistered")}</option>
                      <option value="lastLoginAt">{t("dashboard.userSortLastLogin")}</option>
                    </select>
                    <button type="submit" disabled={searchingUsers}>
                      {searchingUsers ? t("dashboard.userSearching") : t("dashboard.userSearchSubmit")}
                    </button>
                  </form>
                  <div className={styles.searchResults}>
                    {searchingUsers ? <p className={styles.state}>{t("dashboard.userSearchLoading")}</p> : searchedUsers.length === 0 && !usersLoadFailed ? <p className={styles.state}>{t("dashboard.userSearchEmpty")}</p> : (
                      searchedUsers.map((item) => {
                        const fullName = [item.lastName, item.firstName].filter(Boolean).join(" ").trim() || t("dashboard.userSearchUnknown");
                        const phone = [item.indicatif, item.phone].filter(Boolean).join(" ").trim() || "-";
                        const countryLabel = getCountryValueDisplay(item.country, item.codeCountry || "-", dashboardLang);
                        return (
                          <article key={item.id} className={`${styles.commentCard} ${styles.userSearchCard}`}>
                            <header className={styles.commentHeader}>
                              <strong>{fullName}</strong>
                              <div className={styles.userSearchActions}>
                                <span>{item.role || "-"}</span>
                                <button
                                  type="button"
                                  className={styles.userDeleteBtn}
                                  onClick={() => handleDeleteUserCascade(item)}
                                  disabled={deletingUserId === item.id}
                                  title={t("dashboard.userDelete")}
                                  aria-label={t("dashboard.userDelete")}
                                >
                                  <TrashIcon />
                                  <span>{deletingUserId === item.id ? t("dashboard.userDeleting") : t("dashboard.userDelete")}</span>
                                </button>
                              </div>
                            </header>
                            <div className={styles.userSearchDetails}>
                              <span>{t("dashboard.userSearchId")}: {item.id}</span>
                              <span>{t("dashboard.userSearchEmail")}: {item.email || "-"}</span>
                              <span>{t("dashboard.userSearchPhone")}: {phone}</span>
                              <span>{t("dashboard.userSearchStatus")}: {item.status || "-"}</span>
                              <span>{t("dashboard.userSearchChildren")}: {item.nbr_child}</span>
                              <span>{t("dashboard.userSearchCountry")}: {countryLabel}</span>
                              <span>{t("dashboard.userSearchAccount")}: {item.status_compte ? t("dashboard.userSearchAccountActive") : t("dashboard.userSearchAccountInactive")}</span>
                              <span>{t("dashboard.userSearchCreatedAt")}: {item.created_at ? formatDateTime(item.created_at, dashboardLang) : "-"}</span>
                            </div>
                          </article>
                        );
                      })
                    )}
                    {loadingMoreUsers && <p className={styles.state} role="status">{t("dashboard.userSearchLoading")}</p>}
                    {usersLoadFailed && (
                      <button type="button" onClick={() => void loadUsers(submittedUserQueryRef.current, usersNextCursor || undefined)}>
                        {t("dashboard.userSearchRetry")}
                      </button>
                    )}
                    <div ref={usersSentinelRef} className={styles.usersSentinel} aria-hidden="true" />
                  </div>
                </section>
              ) : null}

              {activeSection === "bookings" && canManageRoles ? (
                <section className={`${styles.toolCard} ${styles.formDanger}`}>
                  <div className={styles.toolHeader}>
                    <h2>{t("dashboard.bookingDeleteSectionTitle")}</h2>
                  </div>
                  <form className={styles.form} onSubmit={handleDeleteUserBookings}>
                    <label htmlFor="dashboard-booking-user-id">{t("dashboard.bookingDeleteUserIdLabel")}</label>
                    <input id="dashboard-booking-user-id" type="text" value={bookingUserIdToDelete} onChange={(event) => setBookingUserIdToDelete(event.target.value)} placeholder={t("dashboard.bookingDeleteUserIdPlaceholder")} disabled={deletingUserBookings} />
                    <button type="submit" disabled={deletingUserBookings}>
                      {deletingUserBookings ? t("dashboard.bookingDeleting") : t("dashboard.bookingDeleteSubmit")}
                    </button>
                  </form>
                </section>
              ) : null}

              {activeSection === "countries" && canManageRoles ? (
                <section className={`${styles.toolCard} ${styles.formRole}`}>
                  <div className={styles.toolHeader}>
                    <h2>{t("dashboard.countryAddTitle")}</h2>
                  </div>
                  <form className={styles.form} onSubmit={handleSaveCountry}>
                    <label htmlFor="dashboard-country-name">{t("dashboard.countryNameLabel")}</label>
                    <input
                      id="dashboard-country-name"
                      type="text"
                      value={countryForm.name}
                      onChange={(event) => handleCountryFormChange("name", event.target.value)}
                      placeholder={t("dashboard.countryNamePlaceholder")}
                      disabled={savingCountry}
                    />
                    <label htmlFor="dashboard-country-indicatif">{t("dashboard.countryIndicatifLabel")}</label>
                    <input
                      id="dashboard-country-indicatif"
                      type="text"
                      value={countryForm.indicatif}
                      onChange={(event) => handleCountryFormChange("indicatif", event.target.value)}
                      placeholder={t("dashboard.countryIndicatifPlaceholder")}
                      disabled={savingCountry}
                    />
                    <label htmlFor="dashboard-country-mask">{t("dashboard.countryMaskLabel")}</label>
                    <input
                      id="dashboard-country-mask"
                      type="text"
                      value={countryForm.mask}
                      onChange={(event) => handleCountryFormChange("mask", event.target.value)}
                      placeholder={t("dashboard.countryMaskPlaceholder")}
                      disabled={savingCountry}
                    />
                    <label htmlFor="dashboard-country-code">{t("dashboard.countryCodeLabel")}</label>
                    <input
                      id="dashboard-country-code"
                      type="text"
                      value={countryForm.code}
                      onChange={(event) => handleCountryFormChange("code", event.target.value)}
                      placeholder={t("dashboard.countryCodePlaceholder")}
                      disabled={savingCountry}
                      maxLength={3}
                    />
                    <div className={styles.countryFormActions}>
                      <button type="submit" disabled={savingCountry}>
                        {savingCountry
                          ? editingCountryKey
                            ? t("dashboard.countryUpdating")
                            : t("dashboard.countryAdding")
                          : editingCountryKey
                            ? t("dashboard.countryUpdateSubmit")
                            : t("dashboard.countryAddSubmit")}
                      </button>
                      {editingCountryKey ? (
                        <button type="button" className={styles.secondaryButton} onClick={resetCountryForm} disabled={savingCountry}>
                          {t("dashboard.countryEditCancel")}
                        </button>
                      ) : null}
                    </div>
                  </form>
                  <div className={styles.countryListBlock}>
                    <div className={styles.countryListHeader}>
                      <h3>{t("dashboard.countryListTitle")}</h3>
                      <button type="button" onClick={loadCountries} disabled={countriesLoading}>
                        {countriesLoading ? t("dashboard.countryListLoading") : t("dashboard.countryListRefresh")}
                      </button>
                    </div>
                    {countriesLoading ? (
                      <p className={styles.state}>{t("dashboard.countryListLoading")}</p>
                    ) : countries.length === 0 ? (
                      <p className={styles.state}>{t("dashboard.countryListEmpty")}</p>
                    ) : (
                      <div className={styles.countryList}>
                        {countries.map((country) => {
                          const countryName = getCountryDisplayName(country);
                          const key = String(country.id || country._id || country.code || countryName);
                          const deleteKey = String(country.id || country._id || country.code || "");
                          return (
                            <article key={key} className={styles.countryItem}>
                              <div>
                                <strong>{countryName || "-"}</strong>
                                <span>{[country.code, country.indicatif, country.mask].filter(Boolean).join(" · ")}</span>
                              </div>
                              <button
                                type="button"
                                className={styles.countryEditButton}
                                onClick={() => handleEditCountry(country)}
                                disabled={!deleteKey || savingCountry}
                              >
                                {t("dashboard.countryEdit")}
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDeleteCountry(country)}
                                disabled={!deleteKey || deletingCountryKey === deleteKey}
                              >
                                {deletingCountryKey === deleteKey ? t("dashboard.countryDeleting") : t("dashboard.countryDelete")}
                              </button>
                            </article>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </section>
              ) : null}

              {activeSection === "geo" && canOpenGeoMap ? (
                <section className={`${styles.toolCard} ${styles.formRole}`}>
                  <div className={styles.toolHeader}>
                    <h2>{t("dashboard.geoMapTitle")}</h2>
                  </div>
                  <div className={styles.form}>
                    <p className={styles.state}>{t("dashboard.geoMapDescription")}</p>
                    <button type="button" onClick={handleOpenGeoModal} disabled={geoLoading}>
                      {geoLoading ? t("dashboard.geoMapLoading") : t("dashboard.geoMapOpen")}
                    </button>
                  </div>
                </section>
              ) : null}

              {activeSection === "reports" ? (
                <section className={`${styles.commentsModeration} ${styles.commentsPanel}`}>
                  <div className={styles.commentsModerationHeader}>
                    <h2>{t("dashboard.reportsTitle")}</h2>
                    <button type="button" onClick={loadOwnerReports} disabled={ownerReportsLoading}>
                      {t("dashboard.reportsRefresh")}
                    </button>
                  </div>
                  {ownerReportsLoading ? <p className={styles.state}>{t("dashboard.reportsLoading")}</p> : ownerReports.length === 0 ? <p className={styles.state}>{t("dashboard.reportsEmpty")}</p> : (
                    <div className={styles.commentsList}>
                      {ownerReports.map((report) => (
                        <article key={report.id} className={`${styles.commentCard} ${styles.reportCard}`}>
                          <header className={`${styles.commentHeader} ${styles.reportHeader}`}>
                            <strong>{report.adTitle || t("dashboard.pendingAdUnknownTitle")}</strong>
                            <span className={styles.reportDate}>{formatDateTime(report.createdAt, dashboardLang)}</span>
                          </header>
                          <p className={`${styles.commentAdTitle} ${styles.reportMeta}`}>{t("dashboard.reportsReporter")}: {report.reporterName || report.reporterEmail || "-"}</p>
                          <p className={`${styles.commentAdTitle} ${styles.reportMeta}`}>{t("dashboard.reportsOwner")}: {report.reportedOwnerName || "-"}</p>
                          <p className={`${styles.commentContent} ${styles.reportMessage}`}>{report.message || "-"}</p>
                        </article>
                      ))}
                    </div>
                  )}
                </section>
              ) : null}

              {activeSection === "pending-ads" ? (
                <section className={`${styles.commentsModeration} ${styles.commentsPanel}`}>
                  <div className={styles.commentsModerationHeader}>
                    <h2>{t("dashboard.pendingAdsTitle")}</h2>
                    <button type="button" onClick={loadPendingAds} disabled={pendingAdsLoading}>
                      {t("dashboard.pendingAdsRefresh")}
                    </button>
                  </div>
                  {pendingAdsLoading ? <p className={styles.state}>{t("dashboard.pendingAdsLoading")}</p> : pendingAds.length === 0 ? <p className={styles.state}>{t("dashboard.pendingAdsEmpty")}</p> : (
                    <div className={styles.pendingAdsList}>
                      {pendingAds.map((ad) => (
                        <button
                          key={ad.id}
                          type="button"
                          className={styles.pendingAdItem}
                          onClick={() => {
                            setPendingAdToValidate({ id: ad.id, title: ad.title, address: ad.address });
                          }}
                        >
                          <strong>{ad.title}</strong>
                          <span>{ad.address}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </section>
              ) : null}

              {activeSection === "comments" ? (
                <section className={`${styles.commentsModeration} ${styles.commentsPanel}`}>
                  <div className={styles.commentsModerationHeader}>
                    <h2>{t("dashboard.commentsModerationTitle")}</h2>
                    <button type="button" onClick={loadPendingComments} disabled={commentsLoading}>
                      {t("dashboard.commentsRefresh")}
                    </button>
                  </div>
                  {commentsLoading ? <p className={styles.state}>{t("dashboard.commentsLoading")}</p> : pendingComments.length === 0 ? <p className={styles.state}>{t("dashboard.commentsEmpty")}</p> : (
                    <div className={styles.commentsList}>
                      {pendingComments.map((comment) => (
                        <article key={comment.id} className={styles.commentCard}>
                          <header className={styles.commentHeader}>
                            <strong>{comment.authorName || t("dashboard.commentUnknownAuthor")}</strong>
                            <span>{comment.rating}/5</span>
                          </header>
                          <p className={styles.commentAdTitle}>{comment.adTitle || t("dashboard.commentUnknownAd")}</p>
                          <p className={styles.commentContent}>{comment.content}</p>
                          <button type="button" onClick={() => handleValidateComment(comment.id)} disabled={validatingCommentId === comment.id}>
                            {validatingCommentId === comment.id ? t("dashboard.commentValidating") : t("dashboard.commentValidate")}
                          </button>
                        </article>
                      ))}
                    </div>
                  )}
                </section>
              ) : null}

              {activeSection === "rate-reviews" ? (
                <section className={`${styles.commentsModeration} ${styles.commentsPanel}`}>
                  <div className={styles.commentsModerationHeader}>
                    <h2>{t("dashboard.rateReviewsTitle")}</h2>
                    <button type="button" onClick={() => loadRateReviews({ append: false, cursor: null })} disabled={rateReviewsLoading}>
                      {t("dashboard.rateReviewsRefresh")}
                    </button>
                  </div>
                  <form
                    className={`${styles.form} ${styles.rateReviewsFilters}`}
                    onSubmit={(event) => {
                      event.preventDefault();
                      void loadRateReviews({ append: false, cursor: null });
                    }}
                  >
                    <div className={styles.rateReviewsFilterField}>
                      <label htmlFor="dashboard-rate-review-search">{t("dashboard.rateReviewsSearchLabel")}</label>
                      <input
                        id="dashboard-rate-review-search"
                        type="search"
                        value={rateReviewSearchText}
                        onChange={(event) => setRateReviewSearchText(event.target.value)}
                        placeholder={t("dashboard.rateReviewsSearchPlaceholder")}
                        disabled={rateReviewsLoading}
                      />
                    </div>
                    <div className={styles.rateReviewsFilterField}>
                      <label htmlFor="dashboard-rate-review-status">{t("dashboard.rateReviewsStatusLabel")}</label>
                      <select
                        id="dashboard-rate-review-status"
                        value={rateReviewStatus}
                        onChange={(event) => setRateReviewStatus(event.target.value as "false" | "true" | "all")}
                        disabled={rateReviewsLoading}
                      >
                        <option value="false">{t("dashboard.rateReviewsStatusPending")}</option>
                        <option value="true">{t("dashboard.rateReviewsStatusValidated")}</option>
                        <option value="all">{t("dashboard.rateReviewsStatusAll")}</option>
                      </select>
                    </div>
                    <div className={styles.rateReviewsFilterField}>
                      <label htmlFor="dashboard-rate-review-date-from">{t("dashboard.rateReviewsDateFrom")}</label>
                      <input
                        id="dashboard-rate-review-date-from"
                        type="date"
                        value={rateReviewDateFrom}
                        onChange={(event) => setRateReviewDateFrom(event.target.value)}
                        disabled={rateReviewsLoading}
                      />
                    </div>
                    <div className={styles.rateReviewsFilterField}>
                      <label htmlFor="dashboard-rate-review-date-to">{t("dashboard.rateReviewsDateTo")}</label>
                      <input
                        id="dashboard-rate-review-date-to"
                        type="date"
                        value={rateReviewDateTo}
                        onChange={(event) => setRateReviewDateTo(event.target.value)}
                        disabled={rateReviewsLoading}
                      />
                    </div>
                    <button type="submit" disabled={rateReviewsLoading}>
                      {t("dashboard.rateReviewsApplyFilters")}
                    </button>
                  </form>
                  {rateReviewsLoading ? <p className={styles.state}>{t("dashboard.rateReviewsLoading")}</p> : rateReviews.length === 0 ? <p className={styles.state}>{t("dashboard.rateReviewsEmpty")}</p> : (
                    <div className={styles.commentsList}>
                      {rateReviews.map((review) => {
                        const owner = review.ad?.owner;
                        const reviewer = review.user;
                        const ownerName = [owner?.firstName, owner?.lastName].filter(Boolean).join(" ").trim() || "-";
                        const reviewerName = review.authorName || [reviewer?.firstName, reviewer?.lastName].filter(Boolean).join(" ").trim() || "-";
                        const ownerPhone = [owner?.indicatif, owner?.phone].filter(Boolean).join(" ").trim() || "-";
                        return (
                          <article key={review.id} className={styles.commentCard}>
                            <header className={styles.commentHeader}>
                              <strong>{review.adTitle || review.ad?.title || t("dashboard.commentUnknownAd")}</strong>
                              <span>{review.rating}/5</span>
                            </header>
                            <p className={styles.commentAdTitle}>
                              {t("dashboard.rateReviewsClient")}: {reviewerName}
                              {reviewer?.email ? ` - ${reviewer.email}` : ""}
                            </p>
                            <p className={styles.commentAdTitle}>
                              {t("dashboard.rateReviewsOwner")}: {ownerName}
                              {owner?.email ? ` - ${owner.email}` : ""}
                              {ownerPhone !== "-" ? ` - ${ownerPhone}` : ""}
                            </p>
                            <p className={styles.commentAdTitle}>
                              ID: {review.ad?.id || review.adId || "-"} - {formatDateTime(review.createdAt, dashboardLang)}
                            </p>
                            <p className={styles.commentContent}>{review.content}</p>
                            <button
                              type="button"
                              onClick={() => handleValidateRateReview(review.id, review.valide === true ? false : true)}
                              disabled={validatingRateReviewId === review.id}
                            >
                              {validatingRateReviewId === review.id
                                ? t("dashboard.rateReviewValidating")
                                : review.valide === true
                                  ? t("dashboard.rateReviewInvalidate")
                                  : t("dashboard.rateReviewValidate")}
                            </button>
                          </article>
                        );
                      })}
                      <div ref={rateReviewsSentinelRef} className={styles.sentinel} />
                      {rateReviewsLoadingMore ? <p className={styles.state}>{t("dashboard.rateReviewsLoadingMore")}</p> : null}
                    </div>
                  )}
                </section>
              ) : null}

              {activeSection === "search" ? (
                <section className={`${styles.toolCard} ${styles.formRole}`}>
                  <div className={styles.toolHeader}>
                    <h2>{t("dashboard.commentSearchTitle")}</h2>
                  </div>
                  <form className={styles.form} onSubmit={handleSearchValidatedComments}>
                    <label htmlFor="dashboard-comment-search">{t("dashboard.commentSearchLabel")}</label>
                    <input id="dashboard-comment-search" type="text" value={searchCommentText} onChange={(event) => setSearchCommentText(event.target.value)} placeholder={t("dashboard.commentSearchPlaceholder")} disabled={searchingComments} />
                    <button type="submit" disabled={searchingComments}>
                      {searchingComments ? t("dashboard.commentSearching") : t("dashboard.commentSearchSubmit")}
                    </button>
                  </form>
                  <div className={styles.searchResults}>
                    {searchingComments ? <p className={styles.state}>{t("dashboard.commentSearchLoading")}</p> : searchedComments.length === 0 ? <p className={styles.state}>{t("dashboard.commentSearchEmpty")}</p> : (
                      searchedComments.map((comment) => (
                        <article key={comment.id} className={styles.commentCard}>
                          <header className={styles.commentHeader}>
                            <strong>{comment.authorName || t("dashboard.commentUnknownAuthor")}</strong>
                            <span>{comment.rating}/5</span>
                          </header>
                          <p className={styles.commentAdTitle}>{comment.adTitle || t("dashboard.commentUnknownAd")}</p>
                          <p className={styles.commentContent}>{comment.content}</p>
                          <button type="button" onClick={() => handleInvalidateComment(comment.id)} disabled={invalidatingCommentId === comment.id}>
                            {invalidatingCommentId === comment.id ? t("dashboard.commentInvalidating") : t("dashboard.commentInvalidate")}
                          </button>
                        </article>
                      ))
                    )}
                  </div>
                </section>
              ) : null}
            </section>
          </div>
        </div>
      </section>

      {mobileMenuOpen ? (
        <button
          type="button"
          className={styles.mobileSidebarBackdrop}
          onClick={() => setMobileMenuOpen(false)}
          aria-label={t("dashboard.mobileMenuClose")}
        />
      ) : null}

      {confirmOpen && (
        <div className={styles.overlay} onClick={() => setConfirmOpen(false)}>
          <div className={styles.modal} onClick={(event) => event.stopPropagation()}>
            <h2>{t("dashboard.confirmTitle")}</h2>
            <p>{t("dashboard.confirmText")}</p>
            <div className={styles.actions}>
              <button type="button" onClick={() => setConfirmOpen(false)} disabled={submitting}>
                {t("dashboard.no")}
              </button>
              <button type="button" onClick={handleConfirmDelete} disabled={submitting}>
                {submitting ? t("dashboard.deleting") : t("dashboard.yes")}
              </button>
            </div>
          </div>
        </div>
      )}

      {pendingAdToValidate && (
        <div
          className={styles.overlay}
          onClick={() => {
            if (validatingAd) return;
            setPendingAdToValidate(null);
          }}
        >
          <div className={`${styles.modal} ${validatingAd ? styles.modalBusy : ""}`} onClick={(event) => event.stopPropagation()}>
            <h2>{t("dashboard.pendingAdConfirmTitle")}</h2>
            <p>{t("dashboard.pendingAdConfirmText", { title: pendingAdToValidate.title })}</p>
            {false ? <label className={styles.pendingAdCheckbox}>
              <input
                type="checkbox"
                checked={shouldRefreshPendingAdQuartierCoordinates}
                onChange={(event) => setShouldRefreshPendingAdQuartierCoordinates(event.target.checked)}
                disabled={validatingAd}
              />
              <span>activer la recherche des coordonnées géographiques de l'adresse du quartier de l'annonce</span>
            </label> : null}
            <div className={`${styles.actions} ${validatingAd ? styles.actionsBusy : ""}`}>
              <button
                type="button"
                onClick={() => {
                  setPendingAdToValidate(null);
                  setShouldRefreshPendingAdQuartierCoordinates(false);
                }}
                disabled={validatingAd}
              >
                {t("dashboard.no")}
              </button>
              <button type="button" onClick={handleValidateAdConfirm} disabled={validatingAd}>
                {validatingAd ? t("dashboard.pendingAdValidating") : t("dashboard.yes")}
              </button>
            </div>
          </div>
        </div>
      )}

      {geoModalOpen && (
        <div className={styles.overlay} onClick={handleCloseGeoModal}>
          <div className={`${styles.modal} ${styles.geoModal}`} onClick={(event) => event.stopPropagation()}>
            <div className={styles.geoModalHead}>
              <h2>{t("dashboard.geoMapTitle")}</h2>
              <button type="button" onClick={handleCloseGeoModal} className={styles.geoModalClose}>
                {t("dashboard.geoMapClose")}
              </button>
            </div>
            <div className={styles.geoMapWrapSolo}>
              <div
                ref={geoMapContainerRef}
                className={styles.geoLeafletMap}
                role="img"
                aria-label={t("dashboard.geoMapAriaLabel")}
              />
              {geoLoading && (
                <div className={styles.geoMapLoadingOverlay}>
                  <p className={styles.state}>{t("dashboard.geoMapLoading")}</p>
                </div>
              )}
            </div>
            <div className={styles.geoMapActions}>
              <button
                type="button"
                className={styles.geoActionBtn}
                onClick={() => setGeoCountriesModalOpen(true)}
                disabled={geoLoading}
              >
                {t("dashboard.geoMapShowCountries")}
              </button>
              <button
                type="button"
                className={`${styles.geoActionBtn} ${styles.geoActionBtnReload}`}
                onClick={loadGeoPoints}
                disabled={geoLoading}
                aria-label={t("dashboard.geoMapReload")}
                title={t("dashboard.geoMapReload")}
              >
                ↻
              </button>
            </div>
          </div>
        </div>
      )}

      {geoCountriesModalOpen && (
        <div className={styles.overlay} onClick={() => setGeoCountriesModalOpen(false)}>
          <div className={`${styles.modal} ${styles.geoCountriesModal}`} onClick={(event) => event.stopPropagation()}>
            <div className={styles.geoModalHead}>
              <h2>{t("dashboard.geoMapCountriesTitle")}</h2>
              <button type="button" onClick={() => setGeoCountriesModalOpen(false)} className={styles.geoModalClose}>
                {t("dashboard.geoMapClose")}
              </button>
            </div>
            {geoCountryPoints.length === 0 ? (
              <p className={styles.state}>{t("dashboard.geoMapEmpty")}</p>
            ) : (
              <div className={styles.geoList}>
                {geoCountryPoints.map((point) => {
                  return (
                    <button
                      key={`row-${point.key}`}
                      type="button"
                      className={`${styles.geoListItem} ${styles.geoListItemButton}`}
                      onClick={() => setSelectedGeoCountry(point)}
                    >
                      <strong>{point.country || t("dashboard.geoMapUnknownPlace")}</strong>
                      <span>{point.countryCode || "-"}</span>
                      <span>{point.totalVisits} visite(s) - {point.sampleUsers} profil(s)</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {selectedGeoCountry && (
        <div className={styles.overlay} onClick={() => setSelectedGeoCountry(null)}>
          <div className={`${styles.modal} ${styles.geoCountriesModal}`} onClick={(event) => event.stopPropagation()}>
            <div className={styles.geoModalHead}>
              <h2>
                {t("dashboard.geoMapCountryVisitsTitle", {
                  country: selectedGeoCountry.country || selectedGeoCountry.countryCode || t("dashboard.geoMapUnknownPlace"),
                })}
              </h2>
              <button type="button" onClick={() => setSelectedGeoCountry(null)} className={styles.geoModalClose}>
                {t("dashboard.geoMapClose")}
              </button>
            </div>
            {selectedGeoCountryRows.length === 0 ? (
              <p className={styles.state}>{t("dashboard.geoMapEmpty")}</p>
            ) : (
              <div className={styles.geoVisitRows}>
                {selectedGeoCountryRows.map((row) => (
                  <article key={row.key} className={styles.geoVisitRow}>
                    <strong>{row.city}</strong>
                    <span>{t("dashboard.geoMapIpLabel")}: {row.ip}</span>
                    <span>
                      {t("dashboard.geoMapVisitDateLabel")}:{" "}
                      {row.lastVisitedAt ? formatDateTime(row.lastVisitedAt, dashboardLang) : t("dashboard.geoMapUnknownDate")}
                    </span>
                    <span>{row.visits} {t("dashboard.geoMapVisitsLabel")}</span>
                  </article>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardPage;
